import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronRight } from "lucide-react";
import { formatTime } from "@/utils/formatTime";
import { useTimedExerciseTimer } from "@/hooks/useTimedExerciseTimer";
import { Exercise } from "@/schemas/workout";

interface ExerciseDisplayProps {
  exercise: Exercise; // Should be properly typed
  currentSetIndex: number;
  totalSets: number;
  completeSet: () => void;
  soundEnabled: boolean;
  playSound: (type: "timer" | "rest" | "countdown" | "complete") => void;
  disabled?: boolean;
}

// Type guards for structure
function isTimed(structure: Exercise["structure"]): structure is Extract<Exercise["structure"], { type: "timed" }> {
  return structure.type === "timed";
}
function isCadence(structure: Exercise["structure"]): structure is Extract<Exercise["structure"], { type: "cadence" }> {
  return structure.type === "cadence";
}
function isSets(structure: Exercise["structure"]): structure is Extract<Exercise["structure"], { type: "sets" }> {
  return structure.type === "sets";
}

export default function ExerciseDisplay({
  exercise,
  currentSetIndex,
  totalSets,
  completeSet,
  soundEnabled,
  playSound,
  disabled = false,
}: ExerciseDisplayProps) {
  if (!exercise) return null;

  const { structure } = exercise;

  if (isTimed(structure)) {
    const timedTimer = useTimedExerciseTimer({
      duration: structure.duration,
      soundEnabled,
      playSound,
      onComplete: completeSet,
      isPaused: disabled,
    });
    return (
      <Card className="max-w-md mx-auto mt-8 shadow-lg rounded-xl p-2 bg-background">
        <CardHeader className="pb-2 flex flex-col items-center">
          <CardTitle className="text-3xl mb-1 text-center">{exercise.name}</CardTitle>
          <Badge variant="outline" className="mb-2">Timed</Badge>
        </CardHeader>
        <CardContent>
          <div className="text-6xl font-extrabold mb-6 text-center tracking-tight">
            {formatTime(timedTimer.timeRemaining)}
          </div>
          <div className="flex justify-between text-base text-muted-foreground mb-6 px-2">
            <span>Set {currentSetIndex + 1}/{totalSets}</span>
            <span>Duration: {formatTime(structure.duration)}</span>
          </div>
          <div className="flex flex-col gap-3">
            <Button
              onClick={timedTimer.reset}
              variant="outline"
              size="sm"
              className="w-full"
              disabled={disabled}
            >
              Reset Timer
            </Button>
            <Button
              onClick={completeSet}
              size="lg"
              className="w-full"
              disabled={timedTimer.timeRemaining > 0 || disabled}
            >
              Complete Set <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isCadence(structure)) {
    return (
      <Card className="max-w-md mx-auto mt-8 shadow-lg rounded-xl p-2 bg-background">
        <CardHeader className="pb-2 flex flex-col items-center">
          <CardTitle className="text-3xl mb-1 text-center">{exercise.name}</CardTitle>
          <Badge variant="outline" className="mb-2">Cadence</Badge>
        </CardHeader>
        <CardContent>
          <div className="text-center">
            <p className="text-6xl font-extrabold mb-4 tracking-tight">
              {structure.reps} reps
            </p>
            <p className="text-xl mb-8">Tempo: {structure.tempo}</p>
            <Button
              onClick={completeSet}
              size="lg"
              className="w-full"
              disabled={disabled}
            >
              Complete Set <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isSets(structure)) {
    return (
      <Card className="max-w-md mx-auto mt-8 shadow-lg rounded-xl p-2 bg-background">
        <CardHeader className="pb-2 flex flex-col items-center">
          <CardTitle className="text-3xl mb-1 text-center">{exercise.name}</CardTitle>
          <Badge variant="outline" className="mb-2">Sets</Badge>
        </CardHeader>
        <CardContent>
          <div className="text-center">
            <p className="text-6xl font-extrabold mb-4 tracking-tight">
              {structure.reps} reps
            </p>
            <p className="text-xl mb-8">Set {currentSetIndex + 1} of {structure.sets}</p>
            <Button
              onClick={completeSet}
              size="lg"
              className="w-full"
              disabled={disabled}
            >
              Complete Set <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Fallback for unknown types
  return <div>Unknown exercise type</div>;
}
